// The GIF picker's grid (#736). Every cell has its size and a quiet color at once. A cell gets its picture's address
// only when the shared rule (shared/src/gifGrid.ts, run by GifLoader) lets it: the cells on screen first, top to bottom,
// then about one screen of look-ahead; a cell that leaves that zone before its picture arrives loses the address, which
// cancels the download. A picture that arrived stays for as long as the grid is open (this code keeps none of its own; the
// browser's HTTP cache follows KLIPY's headers). The picture is the smallest of KLIPY's xs and sm that covers the cell,
// across and down, at this screen's scale.
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { gifNeededPixels, gifPickRendition } from "@bloom/shared";
import type { GifAttachment, GifResult, Rendition } from "../gifs.ts";
import { GifLoader } from "../gifLoad.ts";

/** About one screen of look-ahead above and below the picker's scroll area. */
const LOOK_AHEAD = "100% 0px 100% 0px";

function Preview({ r, url, high, onDone }: { r: Rendition; url: string | undefined; high: boolean; onDone: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const img = useRef<HTMLImageElement>(null);
  // A video keeps downloading after its address goes; load() with no address stops it.
  useLayoutEffect(() => {
    if (!url) video.current?.load();
  }, [url]);
  // React drops a removed cell's element with its address still on it, and Chromium goes on downloading a detached
  // picture: a new search, or closing the picker, must take the address off so the download stops.
  useLayoutEffect(() => {
    const node = img.current ?? video.current;
    return () => {
      if (!node) return;
      node.removeAttribute("src");
      if (node instanceof HTMLVideoElement) node.load();
    };
  }, []);
  if (r.format === "video") {
    return <video ref={video} src={url} width={r.width} height={r.height} autoPlay loop muted playsInline preload="auto" onLoadedData={onDone} onError={onDone} />;
  }
  return <img ref={img} src={url} width={r.width} height={r.height} alt="" decoding="async" fetchPriority={high ? "high" : "low"} onLoad={onDone} onError={onDone} />;
}

export function GifGrid({ results, onPick }: { results: GifResult[]; onPick: (gif: GifAttachment) => void }) {
  // One loader per grid: the picker gives the grid a new key for each new search.
  const [loader] = useState(() => new GifLoader());
  useSyncExternalStore(loader.subscribe, loader.getVersion);
  const grid = useRef<HTMLDivElement>(null);
  const cells = useRef(new Map<number, HTMLElement>());
  const observers = useRef<{ visible: IntersectionObserver; near: IntersectionObserver } | null>(null);
  const refs = useRef(new Map<number, (el: HTMLElement | null) => void>());
  // The pixels a cell needs, across and down (the largest seen, so a resize never swaps a loaded picture). The cell crops
  // with `object-fit: cover`, so the picture must cover both.
  const [needed, setNeeded] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const first = cells.current.get(0);
      if (!first) return;
      const box = first.getBoundingClientRect();
      const scale = window.devicePixelRatio || 1;
      setNeeded((n) => {
        const width = Math.max(n.width, gifNeededPixels(box.width, scale));
        const height = Math.max(n.height, gifNeededPixels(box.height, scale));
        return width === n.width && height === n.height ? n : { width, height };
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const root = grid.current?.closest(".gif-grid-wrap") ?? null;
    if (typeof IntersectionObserver === "undefined") return;
    const index = (e: IntersectionObserverEntry) => Number((e.target as HTMLElement).dataset.gifIndex);
    const visible = new IntersectionObserver((es) => loader.batch(() => es.forEach((e) => loader.setVisible(index(e), e.isIntersecting))), { root });
    const near = new IntersectionObserver((es) => loader.batch(() => es.forEach((e) => loader.setNear(index(e), e.isIntersecting))), { root, rootMargin: LOOK_AHEAD });
    observers.current = { visible, near };
    for (const el of cells.current.values()) {
      visible.observe(el);
      near.observe(el);
    }
    return () => {
      visible.disconnect();
      near.disconnect();
      observers.current = null;
    };
  }, [loader]);

  // Without IntersectionObserver every cell counts as on screen, and the limit alone spaces the downloads.
  useEffect(() => {
    if (typeof IntersectionObserver !== "undefined") return;
    loader.batch(() => results.forEach((_, i) => loader.setVisible(i, true)));
  }, [loader, results.length]);

  const refFor = (i: number) => {
    let f = refs.current.get(i);
    if (!f) {
      f = (el) => {
        const o = observers.current;
        const old = cells.current.get(i);
        if (old && old !== el) {
          o?.visible.unobserve(old);
          o?.near.unobserve(old);
        }
        if (el) {
          cells.current.set(i, el);
          o?.visible.observe(el);
          o?.near.observe(el);
        } else cells.current.delete(i);
      };
      refs.current.set(i, f);
    }
    return f;
  };

  return (
    <div className="gif-grid" ref={grid}>
      {results.map((g, i) => {
        const r = g.previews[gifPickRendition(g.previews, needed.width, needed.height)] ?? g.preview;
        return (
          <button key={g.id + g.preview.url} ref={refFor(i)} data-gif-index={i} type="button" className="gif-cell" onClick={() => onPick(g.full)} title={g.title} aria-label={g.title || "GIF"}>
            <Preview r={r} url={loader.has(i) ? r.url : undefined} high={loader.isVisible(i)} onDone={() => loader.done(i)} />
          </button>
        );
      })}
    </div>
  );
}
